import type { SessionStepApiBindingDto, SessionStepDto } from '../../schemas/sessionSchemas';

export type ActiveCardField = 'pan' | 'name' | 'dob' | null;

export function isPanStep(step: SessionStepDto) {
  return /pan/i.test(step.stepType) || /pan/i.test(step.displayName);
}

// Which designer input screen a PAN API binding maps to. Keyed off the
// catalogue operationId (Post_Pan_Verify / _Lite / _Ocr) with a path fallback.
export type PanScreenKind = 'standard' | 'lite' | 'ocr';

/**
 * What to CALL each PAN method when telling the applicant we are switching to it.
 *
 * Applicant-facing wording, deliberately not the binding's `summary` ("PAN Verification",
 * "PAN Lite") — those are our product names and say nothing about what the person has to do.
 * These read as the action instead, so "Let's try with a photo of your PAN card instead"
 * explains the screen that is about to appear.
 *
 * `standard` and `lite` share a label because they share a FORM: the applicant types the same
 * PAN and name either way, and announcing a switch between two identical screens would be
 * noise about our internals.
 */
export const PAN_METHOD_LABEL: Record<PanScreenKind, string> = {
  standard: 'your PAN details',
  lite: 'your PAN details',
  ocr: 'a photo of your PAN card',
};

export function panScreenKind(
  binding: Pick<SessionStepApiBindingDto, 'operationId' | 'path'>,
): PanScreenKind {
  const key = `${binding.operationId} ${binding.path}`.toLowerCase();
  if (key.includes('lite')) return 'lite';
  if (key.includes('ocr')) return 'ocr';
  return 'standard';
}

export function findKey(obj: unknown, key: string): unknown {
  if (obj && typeof obj === 'object') {
    if (!Array.isArray(obj) && key in (obj as Record<string, unknown>))
      return (obj as Record<string, unknown>)[key];
    for (const v of Object.values(obj as Record<string, unknown>)) {
      const found = findKey(v, key);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

export function extractError(resultJson: string | null): string | null {
  if (!resultJson) return null;
  try {
    const parsed = JSON.parse(resultJson);
    for (const key of ['error', 'message', 'detail']) {
      const val = findKey(parsed, key);
      if (val !== undefined && String(val).trim()) return String(val).trim();
    }
    for (const key of ['messages', 'errors']) {
      const arr = findKey(parsed, key);
      if (Array.isArray(arr)) {
        const first = arr.find((m: unknown) => typeof m === 'string' && (m as string).trim());
        if (first) return String(first).trim();
      }
    }
    return null;
  } catch {
    return null;
  }
}

// The human reason the backend attached to a failed/review result
// (`verification_reason`) — e.g. "already verified" vs "did not meet requirements".
export function extractReason(resultJson: string | null): string | null {
  if (!resultJson) return null;
  try {
    const val = findKey(JSON.parse(resultJson), 'verification_reason');
    return val !== undefined && String(val).trim() ? String(val).trim() : null;
  } catch {
    return null;
  }
}

export function parsedInput(inputJson: string | null): Record<string, string> {
  if (!inputJson) return {};
  try {
    const p = JSON.parse(inputJson);
    return p && typeof p === 'object' && !Array.isArray(p) ? (p as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function formatDob(val: string): string {
  if (!val) return '';
  const [y, m, d] = val.split('-');
  return `${d}-${m}-${y}`;
}
